import { useCallback } from "react";
import { useAuth } from "@clerk/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BY_STATUS = {
  401: "Your session has ended. Please sign in again.",
  403: "That isn't available for your role.",
  404: "We couldn't find that. It may have been moved or removed.",
  409: "That couldn't be saved because something changed. Please refresh and try again.",
  422: "Some details need another look. Please check and try again.",
  429: "That's a lot at once. Please wait a moment and try again.",
};

const message = (res, data) => {
  if (typeof data.detail === "string" && res.status < 500) return data.detail;
  if (Array.isArray(data.detail)) {
    const m = String(data.detail[0]?.msg || "").replace(/^Value error, /, "");
    return /^[A-Z][^_{}]*$/.test(m) && !m.startsWith("Field") && !m.startsWith("Input") ? m : BY_STATUS[422];
  }
  return BY_STATUS[res.status] || "Something went wrong on our side. Please try again in a moment.";
};

const send = async (url, opts) => {
  let res;
  try {
    res = await fetch(url, opts);
  } catch {
    throw new Error("We couldn't reach Care. Check your connection and try again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(message(res, data));
  return data;
};

export const useApi = (orgId) => {
  const { getToken } = useAuth();
  return useCallback(
    async (path, { method = "GET", body, form, blob } = {}) => {
      const headers = { Authorization: `Bearer ${await getToken()}` };
      if (!form) headers["Content-Type"] = "application/json";
      if (orgId) headers["X-Org-Id"] = orgId;
      if (blob) {
        const res = await fetch(`${API}${path}`, { headers }).catch(() => null);
        if (!res) throw new Error("We couldn't reach Care. Check your connection and try again.");
        if (!res.ok) throw new Error(message(res, await res.json().catch(() => ({}))));
        return res.blob();
      }
      return send(`${API}${path}`, { method, headers, body: form || (body ? JSON.stringify(body) : undefined) });
    },
    [getToken, orgId]
  );
};

export const publicGet = (path) => send(`${API}${path}`);
